/**
 * ContractFormModal — shartnoma yaratish / tahrirlash modal.
 *
 * Yaratish: store_id, number, valid_from, valid_to, contract_type, branch_id.
 * Tahrirlash: number, valid_from, valid_to, contract_type, branch_id (version lock).
 * Status DERIVED — faqat ko'rsatiladi, klient o'zgartira olmaydi.
 * RBAC: administrator / accountant (backend ham tekshiradi).
 * i18n uz/ru.
 */

import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { UuidHelp } from "@/components/UuidHelp";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { useCreateContract, useUpdateContract } from "../api/contractsApi";
import { useApiError } from "@/hooks/useApiError";
import { toLocalYMD, parseYMD } from "@/utils/date";
import type { ContractOut } from "../types";

// ─── Props ────────────────────────────────────────────────────────────────────

interface ContractFormModalProps {
  opened: boolean;
  onClose: () => void;
  contract?: ContractOut;
}

// ─── Forma qiymatlari ─────────────────────────────────────────────────────────

interface ContractFormValues {
  store_id: string;
  number: string;
  /** YYYY-MM-DD string */
  valid_from: string;
  /** YYYY-MM-DD string */
  valid_to: string;
  /** YYYY-MM-DD string — imzolangan sana (ixtiyoriy) */
  signed_at: string;
  contract_type: string;
  branch_id: string;
}

// ─── Shartnoma turlari ────────────────────────────────────────────────────────

const CONTRACT_TYPES = ["trade", "employment", "service", "other"] as const;

// ─── Sana validatsiya yordamchisi ─────────────────────────────────────────────

function isValidDate(s: string): boolean {
  if (!s) return false;
  const re = /^\d{4}-\d{2}-\d{2}$/;
  if (!re.test(s)) return false;
  const d = new Date(s);
  return !isNaN(d.getTime());
}

// ─── Komponent ────────────────────────────────────────────────────────────────

export function ContractFormModal({
  opened,
  onClose,
  contract,
}: ContractFormModalProps) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useApiError();
  const isEdit = Boolean(contract);

  const createContract = useCreateContract();
  const updateContract = useUpdateContract();

  const contractTypeOptions = [
    { value: "", label: t("contracts.form.type_any") },
    ...CONTRACT_TYPES.map((ct) => ({
      value: ct,
      label: t(`contracts.type.${ct}`),
    })),
  ];

  const form = useForm<ContractFormValues>({
    initialValues: {
      store_id: contract?.store_id ?? "",
      number: contract?.number ?? "",
      valid_from: contract?.valid_from ?? "",
      valid_to: contract?.valid_to ?? "",
      signed_at: contract?.signed_at ?? "",
      contract_type: contract?.contract_type ?? "",
      branch_id: contract?.branch_id ?? "",
    },
    validate: {
      store_id: (v) =>
        !isEdit && !v.trim()
          ? t("contracts.form.store_id_required")
          : null,
      number: (v) =>
        !v.trim() ? t("contracts.form.number_required") : null,
      valid_from: (v) =>
        !isValidDate(v) ? t("contracts.form.valid_from_required") : null,
      valid_to: (v, values) => {
        if (!isValidDate(v)) return t("contracts.form.valid_to_required");
        if (values.valid_from && v < values.valid_from)
          return t("contracts.form.valid_to_before_from");
        return null;
      },
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = async (values: ContractFormValues) => {
    try {
      if (isEdit && contract) {
        await updateContract.mutateAsync({
          id: contract.id,
          data: {
            number: values.number,
            valid_from: values.valid_from,
            valid_to: values.valid_to,
            signed_at: values.signed_at || null,
            contract_type: values.contract_type || null,
            branch_id: values.branch_id || null,
            version: contract.version,
          },
        });
        showSuccess("contracts.messages.updated");
      } else {
        await createContract.mutateAsync({
          store_id: values.store_id,
          number: values.number,
          valid_from: values.valid_from,
          valid_to: values.valid_to,
          signed_at: values.signed_at || null,
          contract_type: values.contract_type || null,
          branch_id: values.branch_id || null,
        });
        showSuccess("contracts.messages.created");
      }
      handleClose();
    } catch (err) {
      showError(err);
    }
  };

  const isPending = createContract.isPending || updateContract.isPending;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={600}>
          {isEdit
            ? t("contracts.form.edit_title")
            : t("contracts.form.create_title")}
        </Text>
      }
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit((v) => { void handleSubmit(v); })}>
        <Stack gap="sm">
          {!isEdit && (
            <TextInput
              label={
                <Group gap={4} component="span">
                  {t("contracts.form.store_id")}
                  <UuidHelp />
                </Group>
              }
              placeholder="UUID"
              description={t("contracts.form.store_id_hint")}
              required
              {...form.getInputProps("store_id")}
            />
          )}

          <TextInput
            label={t("contracts.form.number")}
            placeholder={t("contracts.form.number_placeholder")}
            required
            {...form.getInputProps("number")}
          />

          <Group grow>
            <DateInput
              label={t("contracts.form.valid_from")}
              placeholder="2026-01-01"
              valueFormat="YYYY-MM-DD"
              required
              value={parseYMD(form.values.valid_from)}
              onChange={(date) =>
                form.setFieldValue(
                  "valid_from",
                  date ? toLocalYMD(date) : "",
                )
              }
              error={form.errors.valid_from}
            />
            <DateInput
              label={t("contracts.form.valid_to")}
              placeholder="2027-01-01"
              valueFormat="YYYY-MM-DD"
              required
              value={parseYMD(form.values.valid_to)}
              onChange={(date) =>
                form.setFieldValue(
                  "valid_to",
                  date ? toLocalYMD(date) : "",
                )
              }
              error={form.errors.valid_to}
            />
          </Group>

          <DateInput
            label={t("contracts.form.signed_at", {
              defaultValue: "Imzolangan sana",
            })}
            placeholder={t("contracts.form.signed_at_placeholder", {
              defaultValue: "YYYY-MM-DD (ixtiyoriy)",
            })}
            valueFormat="YYYY-MM-DD"
            clearable
            value={parseYMD(form.values.signed_at)}
            onChange={(date) =>
              form.setFieldValue("signed_at", date ? toLocalYMD(date) : "")
            }
            error={form.errors.signed_at}
          />

          <Select
            label={t("contracts.form.contract_type")}
            data={contractTypeOptions}
            {...form.getInputProps("contract_type")}
            allowDeselect={false}
          />

          <TextInput
            label={
              <Group gap={4} component="span">
                {t("contracts.form.branch_id")}
                <UuidHelp optional />
              </Group>
            }
            placeholder="UUID (ixtiyoriy)"
            description={t("contracts.form.branch_id_hint")}
            {...form.getInputProps("branch_id")}
          />

          {/* supplier_enterprise_id — server o'rnatadi, faqat ma'lumot uchun */}
          {contract?.supplier_enterprise_id ? (
            <TextInput
              label={t("contracts.form.supplier_enterprise_id", {
                defaultValue: "Supplier korxona ID",
              })}
              value={contract.supplier_enterprise_id}
              readOnly
              description={t("contracts.form.supplier_enterprise_id_hint", {
                defaultValue:
                  "Server tomonidan avtomatik o'rnatiladi (sizning korxonangiz).",
              })}
            />
          ) : (
            <Alert
              icon={<IconInfoCircle size={16} />}
              color="blue"
              variant="light"
              p="xs"
            >
              <Text size="xs">
                {t("contracts.form.supplier_enterprise_auto", {
                  defaultValue:
                    "Supplier korxona avtomatik (sizning korxonangiz) — server o'rnatadi.",
                })}
              </Text>
            </Alert>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose} disabled={isPending}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={isPending}>
              {isEdit ? t("common.save") : t("common.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
